import React from 'react';
import { motion } from 'framer-motion';
import { Check, ChevronDown, ArrowRight } from 'lucide-react';
import { useTranslation } from './I18nContext';

export const FeatureExplosion: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="relative py-32 overflow-hidden bg-[#Fdfdfd] dark:bg-[#020617] transition-colors duration-300">
      
      {/* Background subtle gradient mesh */}
      <div className="absolute top-0 left-0 right-0 h-[500px] bg-gradient-to-b from-slate-100/50 to-transparent dark:from-[#0f172a] dark:to-transparent pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6">
        
        {/* --- Header Section (Centered) --- */}
        <div className="text-center mb-20 relative z-20">
            <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-5xl md:text-7xl font-bold text-slate-900 dark:text-white tracking-tight mb-6 leading-[1.1]"
            >
                {t('feature.title1')} <br />
                <span className="text-slate-400 dark:text-slate-600">{t('feature.title2')}</span>
            </motion.h2>
            <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
                className="text-lg text-slate-500 dark:text-slate-400 max-w-xl mx-auto"
            >
                {t('feature.subtitle') + ' '}<span className="text-[#0ea5e9]">{t('feature.highlight')}</span>
                <br/>{t('feature.subtitle2')}
            </motion.p>

            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="mt-8"
            >
                <button className="bg-[#1e293b] dark:bg-white text-white dark:text-[#020617] px-6 py-3 rounded-full font-medium text-sm shadow-xl hover:scale-105 transition-transform">
                    {t('feature.cta')}
                </button>
            </motion.div>
        </div>

        {/* --- The Composition (Paper Stack) --- */}
        <div className="relative h-[600px] w-full max-w-4xl mx-auto perspective-1000">
            
            {/* 1. Left Paper (The Spec Sheet) */}
            <motion.div 
                initial={{ rotate: -5, y: 50, opacity: 0 }}
                whileInView={{ rotate: -2, y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="absolute left-0 md:left-10 top-0 w-[90%] md:w-[45%] aspect-[3/4] bg-white dark:bg-[#1e293b] rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] dark:shadow-none border border-slate-100 dark:border-white/5 p-8 z-10"
            >
                {/* Blue Seal/Badge */}
                <div className="w-16 h-16 bg-[#0ea5e9] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-[#0ea5e9]/30">
                    <div className="w-12 h-12 border-2 border-white/30 rounded-full border-dashed animate-spin-slow"></div>
                </div>

                {/* Header Info */}
                <div className="mb-8">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">FROM</div>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white">HenselX Studio</h3>
                    <p className="text-slate-500 text-sm">architecture@henselx.it</p>
                </div>

                {/* Spec List */}
                <div className="space-y-4 mb-8">
                    <div className="pb-4 border-b border-slate-100 dark:border-white/5">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">TO</div>
                        <div className="flex items-center gap-2">
                             <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xs">C</div>
                             <span className="font-bold text-slate-900 dark:text-white">Client Systems</span>
                        </div>
                    </div>
                    
                    <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-600 dark:text-slate-300">Architecture</span>
                            <span className="font-mono text-slate-900 dark:text-white">On-Premise</span>
                        </div>
                         <div className="flex justify-between text-sm">
                            <span className="text-slate-600 dark:text-slate-300">GPU Cluster</span>
                            <span className="font-mono text-slate-900 dark:text-white">4x H100</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-600 dark:text-slate-300">Model</span>
                            <span className="font-mono text-slate-900 dark:text-white">Llama-3-70b</span>
                        </div>
                    </div>
                </div>

                <div className="mt-auto pt-6 border-t border-slate-100 dark:border-white/5">
                     <div className="flex justify-between items-center">
                         <span className="text-xs font-bold text-slate-400 uppercase">STATUS</span>
                         <span className="text-[#0ea5e9] font-bold text-lg">READY</span>
                     </div>
                </div>
            </motion.div>

            {/* 2. Paperclip (Visual Element) */}
            <motion.div 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="absolute left-[35%] md:left-[40%] -top-4 z-20 w-12 text-slate-400 dark:text-slate-500 rotate-12 drop-shadow-md"
            >
                <svg viewBox="0 0 24 50" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 15 V35 A 5 5 0 0 0 17 35 V10 A 5 5 0 0 0 7 10 V30" />
                </svg>
            </motion.div>

            {/* 3. Right Paper (Ghost/Background) */}
            <motion.div 
                 initial={{ rotate: 5, y: 80, opacity: 0 }}
                 whileInView={{ rotate: 3, y: 20, opacity: 1 }}
                 viewport={{ once: true }}
                 transition={{ duration: 0.8, delay: 0.2 }}
                 className="absolute right-0 md:right-10 top-10 w-[90%] md:w-[45%] aspect-[3/4] bg-[#f8fafc] dark:bg-[#0f172a] rounded-2xl border border-slate-200 dark:border-white/5 p-8 z-0 hidden md:block opacity-50"
            >
                 <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-6 opacity-50">
                    <Check className="text-white w-6 h-6" />
                 </div>
                 <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-4"></div>
                 <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded mb-2"></div>
                 <div className="h-2 w-2/3 bg-slate-100 dark:bg-slate-800 rounded mb-2"></div>
            </motion.div>

            {/* 4. Floating UI Elements (The Dropdowns) */}
            <div className="absolute top-[40%] left-[10%] right-[10%] md:left-[25%] md:right-[25%] z-30 flex flex-col md:flex-row items-center gap-4">
                
                {/* Input Card */}
                <motion.div 
                    initial={{ x: -50, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="bg-white dark:bg-[#1e293b] p-3 rounded-xl shadow-2xl border border-slate-100 dark:border-white/10 flex items-center gap-3 w-full md:w-auto min-w-[200px]"
                >
                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                        <span className="font-bold text-xs">IN</span>
                    </div>
                    <div className="flex-grow">
                        <div className="text-[10px] text-slate-400 font-bold uppercase">Data Source</div>
                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Public Cloud</div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                </motion.div>

                <div className="bg-white dark:bg-[#020617] p-2 rounded-full shadow-lg z-10 border border-slate-100 dark:border-white/10">
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                </div>

                {/* Output Card */}
                <motion.div 
                    initial={{ x: 50, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                    className="bg-white dark:bg-[#1e293b] p-3 rounded-xl shadow-2xl border border-slate-100 dark:border-white/10 flex items-center gap-3 w-full md:w-auto min-w-[200px] relative"
                >
                    <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                        <span className="font-bold text-xs">OUT</span>
                    </div>
                    <div className="flex-grow">
                        <div className="text-[10px] text-slate-400 font-bold uppercase">Processing</div>
                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Local Tensor</div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                    
                    {/* Blue Tape "Secure" */}
                    <div className="absolute -right-6 -bottom-4 bg-[#0ea5e9] text-white text-[10px] font-bold px-8 py-1 rotate-[-15deg] shadow-lg opacity-90 backdrop-blur-sm z-40 clip-path-tape">
                        SECURE
                    </div>
                </motion.div>
            </div>


            {/* 5. The Yellow Post-it */}
            <motion.div 
                 initial={{ scale: 0, rotate: 10 }}
                 whileInView={{ scale: 1, rotate: 6 }}
                 viewport={{ once: true }}
                 transition={{ type: "spring", stiffness: 200, delay: 0.8 }}
                 className="absolute bottom-10 left-[40%] md:left-[50%] z-40"
            >
                <div className="w-48 h-48 bg-[#fef08a] shadow-[0_15px_30px_rgba(0,0,0,0.15)] flex items-center justify-center p-6 transform hover:scale-105 transition-transform duration-300 origin-top-left mask-tape-top">
                    <p className="font-serif italic text-slate-800 text-2xl leading-tight -rotate-3">
                        System live. <br/>
                        <span className="line-through decoration-slate-400/50 decoration-2">No Latency</span> <br/>
                        <span className="text-3xl mt-2 block">Zero Latency!</span>
                        <span className="block text-right mt-2 text-red-500 text-3xl">♡</span>
                    </p>
                </div>
            </motion.div>

        </div>
      </div>
    </section>
  );
};